import {useMemo, useState} from 'react'
import {CalendarIcon} from 'lucide-react'
import {useTranslation} from 'react-i18next'
import {cn} from '@/lib/utils'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {Calendar, type CalendarLabels} from '@/components/ui/calendar'
import {useI18n} from '@/i18n'

// ISO-date format marker — never a user-facing string.
const PLACEHOLDER_ISO_DATE = 'YYYY-MM-DD'
const MOBILE_NARROW_PX = 480

/**
 * Canonical date / datetime picker for the whole app. Wraps the
 * shadcn-style {@link Calendar} primitive in a {@link Popover} on
 * desktop and falls back to the browser's native input below the
 * mobile breakpoint (&lt; 480 px) — because the custom calendar grid
 * has no touch-optimised layout.
 *
 * <p>Set {@code withTime} to add an {@code <input type="time">} inside
 * the popover and switch the native fallback to
 * {@code <input type="datetime-local">}; {@code value} then carries an
 * ISO instant ({@code YYYY-MM-DDTHH:mm:ssZ}) instead of a plain ISO
 * date ({@code YYYY-MM-DD}).
 *
 * <p>Every page that previously rendered raw {@code <input type="date">}
 * or {@code <input type="datetime-local">} (forms, dialogs, filter
 * panels, detail views) should migrate to this component. Issue 355
 * tracks the codebase-wide migration. New pages must NOT hand-roll a
 * date input.
 */
export interface DatePickerProps {
    /** ISO date (`YYYY-MM-DD`) or — when {@link withTime} — ISO instant (`YYYY-MM-DDTHH:mm:ssZ`). Empty for unset. */
    value: string
    /** Fires with an ISO date / instant string or empty string (on manual clear). */
    onChange(iso: string): void
    /** Accessible name for the trigger — already translated. */
    ariaLabel: string
    /** Placeholder / label shown on the trigger when {@link value} is empty. Already translated. */
    placeholder?: string
    /** Disables the trigger without removing it. */
    disabled?: boolean
    /** Adds a time input + switches native fallback to `datetime-local`. {@link value} is then an ISO instant. */
    withTime?: boolean
    /** Clamp the earliest selectable date. Omit for unclamped pickers (filter panel). */
    minDate?: Date
    /** Clamp the latest selectable date. Omit for unclamped pickers (filter panel). */
    maxDate?: Date
    /** Forces the native input branch (mobile narrow, test mode). */
    forceNative?: boolean
    /** Data-testid for the trigger (required). */
    testId: string
    /** Optional CSS classes forwarded to the trigger. */
    className?: string
}

export function DatePicker({
    value,
    onChange,
    ariaLabel,
    placeholder,
    disabled,
    withTime,
    minDate,
    maxDate,
    forceNative,
    testId,
    className,
}: DatePickerProps) {
    const i18n = useI18n()
    const {i18n: i18nextInstance} = useTranslation()
    const appLang = i18nextInstance.language
    const [open, setOpen] = useState(false)
    const calendarLabels = useCalendarLabels()
    const useNative = resolveNative(forceNative)

    if (useNative && withTime) {
        return (
            <Input
                type="datetime-local"
                value={isoToDatetimeLocal(value)}
                disabled={disabled}
                data-testid={testId}
                onChange={(e) => onChange(datetimeLocalToIso(e.target.value))}
                aria-label={ariaLabel}
                lang={appLang}
                className={className}
            />
        )
    }

    if (useNative) {
        return (
            <Input
                type="date"
                value={value}
                disabled={disabled}
                min={minDate ? toIsoDate(minDate) : undefined}
                max={maxDate ? toIsoDate(maxDate) : undefined}
                data-testid={testId}
                onChange={(e) => onChange(e.target.value)}
                placeholder={PLACEHOLDER_ISO_DATE}
                aria-label={ariaLabel}
                lang={appLang}
                className={className}
            />
        )
    }

    const local = withTime ? isoToDatetimeLocal(value) : ''
    const datePart = withTime ? local.slice(0, 10) : value
    const timePart = withTime ? local.slice(11, 16) : ''

    const isOutOfRange = (iso: string): boolean => {
        if (!iso) return false
        if (minDate && iso < toIsoDate(minDate)) return true
        if (maxDate && iso > toIsoDate(maxDate)) return true
        return false
    }

    const emit = (nextDate: string, nextTime: string): void => {
        if (!withTime) {
            if (isOutOfRange(nextDate)) return
            onChange(nextDate)
            setOpen(false)
            return
        }
        const safeTime = nextTime || '00:00'
        // eslint-disable-next-line i18next/no-literal-string -- ISO datetime-local format, not user-visible text
        onChange(datetimeLocalToIso(`${nextDate}T${safeTime}`))
    }

    const triggerLabel = (() => {
        if (withTime) return local ? local.replace('T', ' ') : (i18n.unifiedSearch.panel.pickDateTime as string)
        return value || (placeholder ?? (i18n.unifiedSearch.panel.pickDate as string))
    })()

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    disabled={disabled}
                    data-testid={testId}
                    className={cn(
                        'w-full justify-start text-left font-normal',
                        !value && 'text-muted-foreground',
                        className,
                    )}
                    aria-label={ariaLabel}
                >
                    <CalendarIcon className="mr-2 h-4 w-4" aria-hidden="true"/>
                    {triggerLabel}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                {withTime ? (
                    <div className="flex flex-col gap-2 p-2">
                        <Calendar
                            value={datePart}
                            onSelect={(iso) => emit(iso, timePart)}
                            labels={calendarLabels}
                            data-testid={`${testId}-calendar`}
                        />
                        <Input
                            type="time"
                            value={timePart}
                            onChange={(e) => emit(datePart, e.target.value)}
                            data-testid={`${testId}-time`}
                            aria-label={i18n.unifiedSearch.panel.time as string}
                            lang={appLang}
                            className="w-full"
                        />
                    </div>
                ) : (
                    <Calendar
                        value={value}
                        onSelect={(iso) => emit(iso, '')}
                        labels={calendarLabels}
                        data-testid={`${testId}-calendar`}
                    />
                )}
            </PopoverContent>
        </Popover>
    )
}

function resolveNative(forceNative: boolean | undefined): boolean {
    if (forceNative !== undefined) return forceNative
    if (typeof window === 'undefined') return false
    return window.innerWidth < MOBILE_NARROW_PX
}

function useCalendarLabels(): CalendarLabels {
    const i18n = useI18n()
    return useMemo(() => ({
        prevMonth: i18n.unifiedSearch.calendar.prevMonth as string,
        nextMonth: i18n.unifiedSearch.calendar.nextMonth as string,
        prevYear: i18n.unifiedSearch.calendar.prevYear as string,
        nextYear: i18n.unifiedSearch.calendar.nextYear as string,
        pickYear: i18n.unifiedSearch.calendar.pickYear as string,
        backToDays: i18n.unifiedSearch.calendar.backToDays as string,
        prevDecade: i18n.unifiedSearch.calendar.prevDecade as string,
        nextDecade: i18n.unifiedSearch.calendar.nextDecade as string,
        weekdayHeader: i18n.unifiedSearch.calendar.weekdayHeader as string,
        weekdaysShort: [
            i18n.unifiedSearch.calendar.weekdayShort.sun as string,
            i18n.unifiedSearch.calendar.weekdayShort.mon as string,
            i18n.unifiedSearch.calendar.weekdayShort.tue as string,
            i18n.unifiedSearch.calendar.weekdayShort.wed as string,
            i18n.unifiedSearch.calendar.weekdayShort.thu as string,
            i18n.unifiedSearch.calendar.weekdayShort.fri as string,
            i18n.unifiedSearch.calendar.weekdayShort.sat as string,
        ],
    }), [i18n])
}

function toIsoDate(d: Date): string {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

/**
 * Convert a `<input type="datetime-local">` value (`YYYY-MM-DDTHH:mm`,
 * naive local time) to an ISO instant. Browser parses local zone;
 * `.toISOString()` normalises to UTC.
 */
export function datetimeLocalToIso(local: string): string {
    if (!local) return ''
    const parsed = new Date(local)
    if (Number.isNaN(parsed.getTime())) return ''
    return parsed.toISOString()
}

/**
 * Convert an ISO instant back to `<input type="datetime-local">` shape.
 * Returns empty for empty / unparseable input.
 */
export function isoToDatetimeLocal(iso: string): string {
    if (!iso) return ''
    const parsed = new Date(iso)
    if (Number.isNaN(parsed.getTime())) return ''
    const pad = (n: number) => n.toString().padStart(2, '0')
    // eslint-disable-next-line i18next/no-literal-string -- ISO datetime-local format, not user-visible text
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
}
